import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpen, Download, Loader2, Presentation, Users2 } from "lucide-react";

/**
 * I manuali d'uso, uno per ruolo, e il documento di presentazione.
 *
 * Si generano sul momento dal backend: sono sempre allineati all'app, e non
 * c'e' un file da tenere aggiornato a mano da qualche parte. Ognuno trova in
 * cima il proprio; gli altri restano scaricabili perche' vanno consegnati.
 */
export default function Manuali() {
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [inCorso, setInCorso] = useState(null);

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get("/manuali");
      setDati(data);
    } catch {
      toast.error("Errore nel caricamento dei manuali");
    } finally { setCarico(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const scarica = async (percorso, nomeFile, chiave) => {
    setInCorso(chiave);
    try {
      const { data } = await api.get(percorso, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = nomeFile;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nella generazione del PDF");
    } finally { setInCorso(null); }
  };

  if (carico) {
    return <p className="text-sm text-slate-500">Caricamento...</p>;
  }

  const manuali = dati?.manuali || [];
  const mio = manuali.find((m) => m.codice === dati?.tuo);
  const altri = manuali.filter((m) => m.codice !== dati?.tuo);

  const bottone = (m, principale) => (
    <Button
      onClick={() => scarica(`/manuali/${m.codice}/pdf`,
                             `Manuale SGM APP - ${m.nome}.pdf`, m.codice)}
      disabled={inCorso === m.codice}
      className={principale
        ? "bg-orange-500 hover:bg-orange-600 text-white h-11"
        : "h-10"}
      variant={principale ? "default" : "outline"}
      data-testid={`manuale-${m.codice}`}>
      {inCorso === m.codice
        ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        : <Download className="w-4 h-4 mr-2" />}
      {inCorso === m.codice ? "Preparazione..." : "Scarica il PDF"}
    </Button>
  );

  return (
    <div className="space-y-6" data-testid="manuali-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#0067b2] mb-2">
          Documentazione
        </div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Manuali d&apos;uso</h1>
        <p className="text-sm text-slate-500 mt-1">
          Un manuale per ogni ruolo, con l&apos;indice cliccabile e i rimandi fra i
          capitoli. Si generano adesso, quindi raccontano l&apos;app com&apos;è oggi.
        </p>
      </div>

      {mio && (
        <Card className="border-orange-200 bg-orange-50/40">
          <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-orange-600 mb-1">
                Il tuo manuale
              </div>
              <h2 className="font-display text-xl font-bold flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-orange-500" /> {mio.nome}
              </h2>
              <p className="text-sm text-slate-600 mt-1">{mio.chi}</p>
              <p className="text-xs text-slate-500 mt-1">
                {mio.capitoli} capitoli · scritto sulle pagine che vedi tu
              </p>
            </div>
            <div className="flex-shrink-0">{bottone(mio, true)}</div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <h2 className="font-display font-bold flex items-center gap-2 mb-1">
            <Presentation className="w-4 h-4 text-[#0067b2]" />
            {dati?.presentazione?.titolo || "Presentazione"}
          </h2>
          <p className="text-sm text-slate-600 mb-3">{dati?.presentazione?.chi}</p>
          <Button variant="outline" className="h-10"
                  onClick={() => scarica("/manuali/presentazione/pdf",
                                         "SGM APP - presentazione.pdf", "presentazione")}
                  disabled={inCorso === "presentazione"}
                  data-testid="manuale-presentazione">
            {inCorso === "presentazione"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            {inCorso === "presentazione" ? "Preparazione..." : "Scarica il PDF"}
          </Button>
        </CardContent>
      </Card>

      {/* Gli altri manuali li vede solo la direzione, che è chi li consegna:
          a ciascun altro serve il proprio e basta. */}
      {dati?.tutti && altri.length > 0 && (
      <div>
        <h2 className="font-display font-bold flex items-center gap-2 mb-1">
          <Users2 className="w-4 h-4 text-slate-500" /> Gli altri ruoli
        </h2>
        <p className="text-sm text-slate-500 mb-3">
          Da consegnare a ciascuno insieme al suo account.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {altri.map((m) => (
            <Card key={m.codice} className="border-slate-200">
              <CardContent className="p-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{m.nome}</div>
                  <p className="text-sm text-slate-600 mt-0.5">{m.chi}</p>
                  <p className="text-xs text-slate-400 mt-1">{m.capitoli} capitoli</p>
                </div>
                <div className="flex-shrink-0">{bottone(m, false)}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      )}

      <p className="text-xs text-slate-500">
        I PDF si aprono sul telefono come sul computer: l&apos;indice è cliccabile e
        le scritte in blu dentro i capitoli portano agli altri punti del manuale.
        Versione {dati?.versione}.
      </p>
    </div>
  );
}
